// Firebase-powered API for Zirve Döküm
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updatePassword
} from 'firebase/auth';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase/config';
import { getCollectionName } from '../firebase/branchApi';
import { normalizeOrderDate } from './orderDate';
import { OrderReads } from './orderReads';
import { orderDayPlan } from './orderReadPlan';

const DEFAULT_TEMPLATES = [
  { id: 1, title: 'Has Kabulü Hakkında', body: 'Piyasada dolaşan düşük ayarlı haslar nedeniyle, damgalı olsa dahi küçük parça has kabul edilmeyecektir. Bilginize. Zirve Döküm.' },
  { id: 2, title: 'Cari Altın Hesabı Hatırlatması', body: 'Şirketimizdeki cari altın hesabınız için ödeme yapmanızı rica ederiz. İlginiz için teşekkür ederiz. Zirve Döküm.' },
  { id: 3, title: 'Döküm Öncesi Altın Ödemesi', body: 'Bugün dökülecek ürünleriniz için altın ödemenizi yapmanızı rica ederiz. Hayırlı günler diler, teşekkür ederiz. Zirve Döküm.' },
  { id: 4, title: 'Hazır Dökümleriniz Var', body: 'Hazır dökümleriniz var, aldırabilirsiniz. Zirve Döküm.' }
];

const uploadedPhotoCache = new Map();
let ordersUnsub = null;
let usersUnsub = null;
let orderReads = null;
const orderDateScopes = new Map();

const Api = {
  user: null,
  csrfToken: 'firebase-session-token',
  desktopClientId: '',

  isManager(user = this.user) {
    return Boolean(user && ['admin', 'branch'].includes(user.role));
  },

  canManageBranch(user = this.user) {
    return this.isManager(user) && Boolean(user.canManageBranch !== false);
  },

  home(user = this.user) {
    return this.isManager(user) ? '/admin' : '/customer';
  },

  today() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());
    return ['year', 'month', 'day'].map(type => parts.find(p => p.type === type).value).join('-');
  },

  createUploadKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'key-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
  },

  refresh() {
    orderReads?.invalidate();
    document.dispatchEvent(new CustomEvent('orders-changed'));
  },

  getActiveBranchId() {
    const selected = localStorage.getItem('selectedBranch');
    return selected === 'vvG8ruigVsJChISbvnNM' ? 2 : 1;
  },

  getActiveBranchName() {
    return this.getActiveBranchId() === 2 ? 'Kent' : 'Çarşı';
  },

  getActiveBranchDocId() {
    return this.getActiveBranchId() === 2 ? 'vvG8ruigVsJChISbvnNM' : 'UIhoz0LQo077vwperTau';
  },

  async loadCurrentUser(providedFbUser = null) {
    let fbUser = providedFbUser || auth.currentUser;
    if (!fbUser) {
      fbUser = await new Promise((resolve) => {
        const unsub = onAuthStateChanged(auth, (u) => {
          unsub();
          resolve(u);
        });
        setTimeout(() => {
          unsub();
          resolve(auth.currentUser);
        }, 1200);
      });
    }

    if (!fbUser) {
      this.user = null;
      return null;
    }

    try {
      const branchUsersCol = getCollectionName('users');
      let userData = null;
      let userDocId = fbUser.uid;

      // 1. Check by field uid in branch users collection
      const qBranchUid = query(collection(db, branchUsersCol), where('uid', '==', fbUser.uid));
      const snapBranchUid = await getDocs(qBranchUid);
      if (!snapBranchUid.empty) {
        userData = snapBranchUid.docs[0].data();
        userDocId = snapBranchUid.docs[0].id;
      } else {
        // 2. Check by email in branch users collection
        const qBranchEmail = query(collection(db, branchUsersCol), where('email', '==', fbUser.email));
        const snapBranchEmail = await getDocs(qBranchEmail);
        if (!snapBranchEmail.empty) {
          userData = snapBranchEmail.docs[0].data();
          userDocId = snapBranchEmail.docs[0].id;
        } else {
          // 3. Check in users-2
          const qU2 = query(collection(db, 'users-2'), where('email', '==', fbUser.email));
          const snapU2 = await getDocs(qU2);
          if (!snapU2.empty) {
            userData = snapU2.docs[0].data();
            userDocId = snapU2.docs[0].id;
          } else {
            // 4. Check in users
            const qU1 = query(collection(db, 'users'), where('email', '==', fbUser.email));
            const snapU1 = await getDocs(qU1);
            if (!snapU1.empty) {
              userData = snapU1.docs[0].data();
              userDocId = snapU1.docs[0].id;
            }
          }
        }
      }

      const role = userData?.role || (fbUser.email?.includes('admin') || fbUser.email === 'casska@msn.com' ? 'admin' : 'customer');
      const displayName = userData?.firstName && userData?.lastName
        ? `${userData.firstName} ${userData.lastName}`.trim()
        : (userData?.firstName || userData?.displayName || fbUser.displayName || userData?.companyName || fbUser.email?.split('@')[0] || 'Kullanıcı');

      const theme = userData?.theme || localStorage.getItem('zirve-theme') || 'dark';
      const themeLightVariant = userData?.themeLightVariant || 'ice';
      const language = userData?.language || localStorage.getItem('zirve-public-language') || 'tr';

      const account = {
        id: userDocId,
        uid: fbUser.uid,
        username: userData?.username || fbUser.email || 'kullanici',
        displayName,
        role,
        branchId: this.getActiveBranchId(),
        branchName: this.getActiveBranchName(),
        branches: [
          { id: 1, name: 'Çarşı' },
          { id: 2, name: 'Kent' }
        ],
        canSwitchBranch: true,
        canManageBranch: role === 'admin',
        theme,
        themeLightVariant,
        language,
        avatarUrl: userData?.avatarUrl || fbUser.photoURL || null,
        version: userData?.version || 1
      };

      this.user = account;
      this.initRealtimeSync();
      return account;
    } catch (err) {
      console.error('Account fetch error:', err);
      const fallback = {
        id: fbUser.uid,
        uid: fbUser.uid,
        username: fbUser.email,
        displayName: fbUser.displayName || fbUser.email?.split('@')[0] || 'Yönetici',
        role: fbUser.email === 'casska@msn.com' ? 'admin' : 'customer',
        branchId: this.getActiveBranchId(),
        branchName: this.getActiveBranchName(),
        branches: [{ id: 1, name: 'Çarşı' }, { id: 2, name: 'Kent' }],
        canSwitchBranch: true,
        canManageBranch: true,
        theme: 'dark',
        language: 'tr',
        version: 1
      };
      this.user = fallback;
      return fallback;
    }
  },

  subscribeOrdersDate(date) {
    if (date) orderDayPlan(date);
    const token = {};
    orderDateScopes.set(token,date || null);
    if (this.user) this.initRealtimeSync();
    return () => {
      if (orderDateScopes.delete(token) && this.user) this.initRealtimeSync();
    };
  },

  initRealtimeSync() {
    if (ordersUnsub) { ordersUnsub(); ordersUnsub = null; }
    if (usersUnsub) { usersUnsub(); usersUnsub = null; }

    const ordersCol = getCollectionName('orders');
    const usersCol = getCollectionName('users');

    try {
      const account=this.user;
      const check=()=>{
        if(account!==this.user || ordersCol!==getCollectionName('orders')) throw new Error('Hesap veya şube değişti. Yeniden açın.');
      };
      orderReads=new OrderReads(db,ordersCol,check,()=>document.dispatchEvent(new Event('orders-changed')));
      const reader=orderReads;
      const dates=[...new Set(orderDateScopes.values())];
      (!dates.length ? [this.today()] : dates.includes(null) ? [null] : dates).forEach(date=>reader.listen(date));
      ordersUnsub=()=>reader.close();

      usersUnsub = onSnapshot(collection(db, usersCol), () => {
        document.dispatchEvent(new Event('customers-changed'));
      }, (err) => console.warn('Users sync notice:', err));
    } catch (e) {
      console.warn('Realtime sync setup:', e);
    }
  },

  async request(url, options = {}) {
    const cleanUrl = url.split('?')[0];
    const params = new URLSearchParams(url.includes('?') ? url.split('?')[1] : '');

    if (cleanUrl === '/api/me') {
      const user = await this.loadCurrentUser();
      if (!user) {
        const error = new Error('Lütfen yeniden giriş yapın.');
        error.status = 401;
        throw error;
      }
      return { user, csrfToken: this.csrfToken };
    }

    if (cleanUrl === '/api/orders') {
      const ordersCol = getCollectionName('orders');
      const date=params.get('date') || null;
      if(date) orderDayPlan(date);
      const account=this.user;
      const reader=orderReads;
      // Full archive watching starts only when a report/history actually requests it.
      if(!date && reader && !reader.closed && reader.name===ordersCol) reader.listen(null);
      const snap = reader && !reader.closed && reader.name===ordersCol
        ? await reader.read(date)
        : await new OrderReads(db,ordersCol,()=>{},()=>{}).read(date);
      const open=params.get('open');
      if(open && !snap.docs.some(d=>d.id===open)) {
        if(['.','..'].includes(open) || open.includes('/') || open.includes('\0')) throw new Error('Kayıt kimliği geçersiz.');
        const linked=await getDoc(doc(db,ordersCol,open));
        if(linked.exists()) snap.docs.push(linked);
      }
      if(account!==this.user || ordersCol!==getCollectionName('orders')) throw new Error('Hesap veya şube değişti. Yeniden açın.');
      let orders = snap.docs.flatMap(docSnap => {
        const d = docSnap.data();
        // An empty Firestore document is not an order; keep the source untouched.
        if (Object.keys(d).length === 0) return [];
        const formattedDate = normalizeOrderDate(d.date);
        if(date && formattedDate!==date && docSnap.id!==open) return [];

        return [{
          id: docSnap.id,
          title: d.title || `Döküm #${docSnap.id}`,
          customerId: d.customerId || null,
          customerName: d.customerName || (d.customerId ? 'Müşteri' : 'Müşteri Seçilmedi'),
          customerUsername: d.customerUsername || '',
          karat: d.karat ? String(d.karat) : '',
          color: d.color || '',
          date: formattedDate,
          completed: Boolean(d.completed === true || d.state === true),
          urgent: Boolean(d.urgent),
          photoUrl: d.photoUrl || d.image || '/assets/casting 2.png',
          createdBy: d.createdBy || 'Yönetim',
          updatedBy: d.updatedBy || 'Yönetim',
          version: d.version || 1,
          infoRequestedAt: d.infoRequestedAt || null
        }];
      });

      // Filter by customer if logged in as customer
      if (this.user && this.user.role === 'customer') {
        const uid = this.user.id || this.user.uid;
        const uEmail = (this.user.username || '').toLowerCase();
        orders = orders.filter(o => 
          String(o.customerId) === String(uid) ||
          (o.customerUsername && o.customerUsername.toLowerCase() === uEmail)
        );
      }

      return { orders };
    }

    if (cleanUrl === '/api/customers' || cleanUrl === '/api/customer-options') {
      const usersCol = getCollectionName('users');
      const snap = await getDocs(collection(db, usersCol));
      
      // Also get orders to compute totals
      const ordersRes = cleanUrl === '/api/customer-options' ? {orders:[]} : await this.request('/api/orders');
      const allOrders = ordersRes.orders || [];

      const customers = snap.docs
        .map(docSnap => {
          const d = docSnap.data();
          const name = d.firstName && d.lastName
            ? `${d.firstName} ${d.lastName}`.trim()
            : (d.displayName || d.companyName || d.name || d.username || d.email);
          const username = d.username || d.email || docSnap.id;
          const cId = docSnap.id;
          const cOrders = allOrders.filter(o => String(o.customerId) === String(cId) || (o.customerUsername && o.customerUsername.toLowerCase() === username.toLowerCase()));

          return {
            id: cId,
            name: name || 'İsimsiz Müşteri',
            username,
            role: d.role || 'customer',
            blacklisted: Boolean(d.blacklisted),
            version: d.version || 1,
            total: cOrders.length,
            completed: cOrders.filter(o => o.completed).length
          };
        })
        .filter(c => c.role === 'customer' || !['admin', 'branch'].includes(c.role));

      return { customers };
    }

    if (cleanUrl === '/api/customers/notifications/templates') {
      return { templates: DEFAULT_TEMPLATES };
    }

    if (cleanUrl === '/api/notifications') {
      try {
        const notifsSnap = await getDocs(collection(db, 'notifications'));
        let notifications = notifsSnap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));

        if (this.user && this.user.role === 'customer') {
          notifications = notifications.filter(n => 
            n.customerId === this.user.id || n.customerId === this.user.uid || n.target === 'all'
          );
        } else if (this.user && this.isManager()) {
          notifications = notifications.filter(n => 
            !n.branchId || n.branchId === this.getActiveBranchId()
          );
        }

        notifications.sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0));
        const unread = notifications.filter(n => !n.readAt).length;

        return { notifications, unread, hasMore: false };
      } catch (err) {
        return { notifications: [], unread: 0, hasMore: false };
      }
    }

    if (cleanUrl.startsWith('/api/notifications/')) {
      const id = cleanUrl.replace('/api/notifications/', '');
      const docSnap = await getDoc(doc(db, 'notifications', id));
      if (docSnap.exists()) {
        const notification = { id: docSnap.id, ...docSnap.data() };
        let orders = [];
        if (notification.orderId) {
          const orderDoc = await getDoc(doc(db, getCollectionName('orders'), notification.orderId));
          if (orderDoc.exists()) orders = [{ id: orderDoc.id, ...orderDoc.data() }];
        }
        return { notification, orders };
      }
      throw new Error('Bildirim bulunamadı.');
    }

    if (cleanUrl === '/api/push/config') {
      return { enabled: false };
    }

    if (cleanUrl === '/api/metrics' || cleanUrl === '/api/order-summary') {
      const ordersRes = await this.request('/api/orders');
      let orders = ordersRes.orders || [];
      const fromParam = params.get('from');
      const toParam = params.get('to');
      if (fromParam && toParam) {
        orders = orders.filter(o => o.date >= fromParam && o.date <= toParam);
      }

      const total = orders.length;
      const completed = orders.filter(o => o.completed).length;
      const pending = total - completed;
      const rate = total ? (completed / total) * 100 : 0;
      const unassigned = orders.filter(o => !o.customerId).length;

      // Group timeline by date
      const dateMap = new Map();
      for (const o of orders) {
        const d = o.date || this.today();
        if (!dateMap.has(d)) dateMap.set(d, { date: d, total: 0, completed: 0, pending: 0 });
        const row = dateMap.get(d);
        row.total++;
        if (o.completed) row.completed++; else row.pending++;
      }
      const timeline = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));

      // Karats breakdown
      const karatMap = new Map();
      for (const o of orders) {
        const k = o.karat ? (o.karat === 'Gümüş' ? 'Gümüş' : o.karat + ' Ayar') : 'Belirtilmedi';
        karatMap.set(k, (karatMap.get(k) || 0) + 1);
      }
      const karats = Array.from(karatMap.entries()).map(([label, value]) => ({
        label,
        value,
        percent: total ? Math.round((value / total) * 100) : 0
      })).sort((a, b) => b.value - a.value);

      // Colors breakdown
      const colorMap = new Map();
      for (const o of orders) {
        const c = o.color || 'Belirtilmedi';
        colorMap.set(c, (colorMap.get(c) || 0) + 1);
      }
      const colors = Array.from(colorMap.entries()).map(([label, value]) => ({
        label,
        value,
        percent: total ? Math.round((value / total) * 100) : 0
      })).sort((a, b) => b.value - a.value);

      // Customers breakdown
      const custMap = new Map();
      for (const o of orders) {
        const cId = o.customerId;
        const cName = o.customerName || 'Müşteri Seçilmedi';
        if (!custMap.has(cId)) custMap.set(cId, { id: cId, name: cName, total: 0, completed: 0, pending: 0 });
        const row = custMap.get(cId);
        row.total++;
        if (o.completed) row.completed++; else row.pending++;
      }
      const custRows = Array.from(custMap.values()).map(r => ({
        ...r,
        rate: r.total ? (r.completed / r.total) * 100 : 0
      })).sort((a, b) => b.total - a.total);

      // Combinations (karat + color)
      const combMap = new Map();
      for (const o of orders) {
        const label = `${o.karat ? o.karat + ' Ayar' : 'Ayar bekliyor'} · ${o.color || 'Renk bekliyor'}`;
        combMap.set(label, (combMap.get(label) || 0) + 1);
      }
      const combinations = Array.from(combMap.entries()).map(([label, value]) => ({
        label,
        value,
        count: value,
        percent: total ? Math.round((value / total) * 100) : 0
      })).sort((a, b) => b.value - a.value).slice(0, 10);

      return {
        summary: {
          total,
          completed,
          pending,
          rate,
          customers: custRows.filter(r => r.id !== null).length,
          unassigned
        },
        branch: {
          id: this.getActiveBranchId(),
          name: this.getActiveBranchName()
        },
        range: {
          from: fromParam || this.today(),
          to: toParam || this.today(),
          granularity: 'day',
          monthly: false
        },
        timeline,
        karats,
        colors,
        customers: custRows,
        combinations,
        generatedAt: new Date().toISOString()
      };
    }

    return {};
  },

  async mutate(url, method = 'POST', body = {}) {
    const cleanUrl = url.split('?')[0];

    // Auth: Login
    if (cleanUrl === '/api/login') {
      const input = (body.username || body.email || '').trim();
      const password = (body.password || '').trim();
      const selectedBranch = body.branch || localStorage.getItem('selectedBranch') || 'UIhoz0LQo077vwperTau';

      // Şube tercihini hemen kaydet
      localStorage.setItem('selectedBranch', selectedBranch);

      let email = input;

      // Eğer kullanıcı adı girildiyse (@ içermiyorsa), Firestore'dan e-postayı bul
      if (!input.includes('@')) {
        const collectionsToCheck = selectedBranch === 'vvG8ruigVsJChISbvnNM'
          ? ['users-2', 'users']
          : ['users', 'users-2'];

        let matchedEmail = null;
        for (const colName of collectionsToCheck) {
          try {
            const snap = await getDocs(collection(db, colName));
            for (const docSnap of snap.docs) {
              const d = docSnap.data();
              const lowerInput = input.toLowerCase();
              const dEmail = (d.email || '').toLowerCase();
              const dFirst = (d.firstName || '').toLowerCase();
              const dLast = (d.lastName || '').toLowerCase();
              const dFull = `${dFirst} ${dLast}`.trim();
              const dUser = (d.username || '').toLowerCase();

              if (
                dEmail.split('@')[0] === lowerInput ||
                dEmail === lowerInput ||
                dUser === lowerInput ||
                dFirst === lowerInput ||
                dFull === lowerInput
              ) {
                matchedEmail = d.email;
                break;
              }
            }
            if (matchedEmail) break;
          } catch (e) {
            console.warn('User search error in', colName, e);
          }
        }

        if (matchedEmail) {
          email = matchedEmail;
        } else if (!input.includes('@')) {
          email = `${input}@zirve.com`;
        }
      }

      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const fbUser = userCredential.user;

        localStorage.setItem('token', fbUser.accessToken || 'token');
        localStorage.setItem('userId', fbUser.uid);
        localStorage.setItem('selectedBranch', selectedBranch);

        const account = await this.loadCurrentUser(fbUser);
        this.user = account;
        return { user: account, csrfToken: this.csrfToken };
      } catch (err) {
        console.error('Firebase Auth Login Hatası:', err.code, err.message);
        let errorMsg = 'E-posta veya şifre hatalı.';
        if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
          errorMsg = 'Girdiğiniz şifre hatalı. Lütfen kontrol edin.';
        } else if (err.code === 'auth/user-not-found') {
          errorMsg = `"${email}" adresiyle kayıtlı kullanıcı bulunamadı.`;
        } else if (err.code === 'auth/invalid-email') {
          errorMsg = 'Lütfen geçerli bir e-posta adresi girin.';
        } else if (err.code === 'auth/too-many-requests') {
          errorMsg = 'Çok fazla başarısız deneme yapıldı. Lütfen biraz sonra tekrar deneyin.';
        }
        throw new Error(errorMsg);
      }
    }

    // Auth: Logout
    if (cleanUrl === '/api/logout') {
      await signOut(auth);
      this.user = null;
      return { ok: true };
    }

    // Branch Switch
    if (cleanUrl === '/api/branch') {
      const bId = Number(body.branchId);
      const branchDocId = bId === 2 ? 'vvG8ruigVsJChISbvnNM' : 'UIhoz0LQo077vwperTau';
      localStorage.setItem('selectedBranch', branchDocId);
      await this.loadCurrentUser();
      document.dispatchEvent(new Event('branch-changed'));
      document.dispatchEvent(new Event('orders-changed'));
      document.dispatchEvent(new Event('customers-changed'));
      return { user: this.user };
    }

    // Orders: Create Batch Commit
    if (cleanUrl === '/api/orders/commit-batch') {
      const ordersCol = getCollectionName('orders');
      const { date, karat, color, completed, urgent, uploadKeys } = body;
      const count = uploadKeys.length;

      for (const key of uploadKeys) {
        const photoUrl = uploadedPhotoCache.get(key) || '/assets/casting 2.png';
        await addDoc(collection(db, ordersCol), {
          title: 'Döküm Siparişi',
          customerId: body.customerId || null,
          customerName: body.customerId ? 'Müşteri' : 'Müşteri Seçilmedi',
          karat: karat || '',
          color: color || '',
          date: date || this.today(),
          completed: Boolean(completed),
          state: Boolean(completed),
          urgent: Boolean(urgent),
          photoUrl,
          image: photoUrl,
          createdBy: this.user?.displayName || 'Yönetim',
          updatedBy: this.user?.displayName || 'Yönetim',
          createdAt: new Date().toISOString(),
          version: 1
        });
      }

      this.refresh();
      return { count };
    }

    // Orders: Single update (PATCH)
    if (cleanUrl.startsWith('/api/orders/') && method === 'PATCH') {
      const id = cleanUrl.replace('/api/orders/', '');
      const ordersCol = getCollectionName('orders');
      const orderRef = doc(db, ordersCol, id);

      let photoUrl = body.photoUrl;
      if (body.photo && body.photo.startsWith('data:image')) {
        const key = this.createUploadKey();
        const uploadRes = await this.uploadPhoto(body.photo, key, () => {});
        photoUrl = uploadRes.photoUrl;
      }

      const updates = {
        updatedBy: this.user?.displayName || 'Yönetim',
        updatedAt: new Date().toISOString()
      };

      if (body.customerId !== undefined) {
        updates.customerId = body.customerId;
        if (!body.customerId) {
          updates.customerName = 'Müşteri Seçilmedi';
        } else {
          // fetch customer name
          const custDoc = await getDoc(doc(db, getCollectionName('users'), String(body.customerId)));
          if (custDoc.exists()) {
            const cd = custDoc.data();
            updates.customerName = cd.firstName ? `${cd.firstName} ${cd.lastName || ''}`.trim() : (cd.displayName || cd.username || 'Müşteri');
          }
        }
      }
      if (body.karat !== undefined) updates.karat = body.karat;
      if (body.color !== undefined) updates.color = body.color;
      if (body.date !== undefined) updates.date = body.date;
      if (body.completed !== undefined) {
        updates.completed = body.completed;
        updates.state = body.completed;
      }
      if (body.urgent !== undefined) updates.urgent = body.urgent;
      if (photoUrl) {
        updates.photoUrl = photoUrl;
        updates.image = photoUrl;
      }
      updates.version = (body.version || 1) + 1;

      await updateDoc(orderRef, updates);
      this.refresh();
      const updatedSnap = await getDoc(orderRef);
      return { order: { id: updatedSnap.id, ...updatedSnap.data() } };
    }

    // Orders: Delete Single
    if (cleanUrl.startsWith('/api/orders/') && method === 'DELETE') {
      const id = cleanUrl.replace('/api/orders/', '');
      const ordersCol = getCollectionName('orders');
      await deleteDoc(doc(db, ordersCol, id));
      this.refresh();
      return { ok: true };
    }

    // Orders: Delete Batch
    if (cleanUrl === '/api/orders/delete-batch') {
      const ordersCol = getCollectionName('orders');
      const items = body.orders || [];
      for (const item of items) {
        await deleteDoc(doc(db, ordersCol, item.id));
      }
      this.refresh();
      return { ok: true };
    }

    // Orders: Info Request ("Bilgi Al")
    if (cleanUrl.match(/^\/api\/orders\/[^/]+\/info$/)) {
      const id = cleanUrl.split('/')[3];
      const ordersCol = getCollectionName('orders');
      const orderRef = doc(db, ordersCol, id);
      const sentAt = Date.now();

      await updateDoc(orderRef, { infoRequestedAt: sentAt });

      await addDoc(collection(db, 'notifications'), {
        title: 'Döküm Hakkında Bilgi Talebi',
        body: `Müşteri, Döküm #${id} hakkında bilgi almak istiyor.`,
        orderId: id,
        customerId: this.user?.id || null,
        customerName: this.user?.displayName || 'Müşteri',
        branchId: this.getActiveBranchId(),
        sentAt,
        readAt: null
      });

      document.dispatchEvent(new Event('notifications-changed'));
      return { sentAt, repeated: false };
    }

    // Customers: Create
    if (cleanUrl === '/api/customers' && method === 'POST') {
      const usersCol = getCollectionName('users');
      const email = body.username.includes('@') ? body.username : `${body.username}@zirvedokum.com`;
      
      let newUid = this.createUploadKey();
      try {
        const userRes = await createUserWithEmailAndPassword(auth, email, body.password);
        newUid = userRes.user.uid;
      } catch (authErr) {
        // If user already exists in Auth, generate unique ID for Firestore document
      }

      const newCustomer = {
        uid: newUid,
        email,
        username: body.username,
        firstName: body.name,
        displayName: body.name,
        role: 'customer',
        blacklisted: false,
        version: 1,
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, usersCol), newCustomer);
      document.dispatchEvent(new Event('customers-changed'));
      return { customer: { id: docRef.id, ...newCustomer } };
    }

    // Customers: Update (PATCH)
    if (cleanUrl.startsWith('/api/customers/') && method === 'PATCH' && !cleanUrl.includes('blacklist')) {
      const id = cleanUrl.replace('/api/customers/', '');
      const usersCol = getCollectionName('users');
      const custRef = doc(db, usersCol, id);

      const updates = {
        displayName: body.name,
        firstName: body.name,
        username: body.username,
        version: (body.version || 1) + 1
      };

      await updateDoc(custRef, updates);
      document.dispatchEvent(new Event('customers-changed'));
      return { customer: { id, ...updates } };
    }

    // Customers: Blacklist
    if (cleanUrl.match(/^\/api\/customers\/[^/]+\/blacklist$/)) {
      const id = cleanUrl.split('/')[3];
      const usersCol = getCollectionName('users');
      const custRef = doc(db, usersCol, id);

      const blacklisted = Boolean(body.blacklisted);
      await updateDoc(custRef, {
        blacklisted,
        version: (body.version || 1) + 1
      });

      const updatedSnap = await getDoc(custRef);
      document.dispatchEvent(new Event('customers-changed'));
      document.dispatchEvent(new Event('orders-changed'));
      return { customer: { id: updatedSnap.id, ...updatedSnap.data() } };
    }

    // Customers: Delete
    if (cleanUrl.startsWith('/api/customers/') && method === 'DELETE') {
      const id = cleanUrl.replace('/api/customers/', '');
      const usersCol = getCollectionName('users');
      await deleteDoc(doc(db, usersCol, id));

      // Unassign customer's orders
      const ordersCol = getCollectionName('orders');
      const q = query(collection(db, ordersCol), where('customerId', '==', id));
      const snap = await getDocs(q);
      for (const orderDoc of snap.docs) {
        await updateDoc(orderDoc.ref, {
          customerId: null,
          customerName: 'Müşteri Seçilmedi'
        });
      }

      document.dispatchEvent(new Event('customers-changed'));
      this.refresh();
      return { unassignedOrders: snap.size };
    }

    // Notifications: Send Manual Notification
    if (cleanUrl === '/api/customers/notifications') {
      const template = DEFAULT_TEMPLATES.find(t => t.id === Number(body.templateId)) || {
        title: 'Zirve Döküm Bilgilendirme',
        body: 'Siparişleriniz güncellendi.'
      };

      const custSnap = await getDoc(doc(db, getCollectionName('users'), String(body.customerId)));
      const custName = custSnap.exists() ? (custSnap.data().displayName || custSnap.data().username) : 'Müşteri';

      await addDoc(collection(db, 'notifications'), {
        title: template.title,
        body: template.body,
        customerId: body.customerId,
        customerName: custName,
        branchId: this.getActiveBranchId(),
        sentAt: Date.now(),
        readAt: null
      });

      document.dispatchEvent(new Event('notifications-changed'));
      return { customerName: custName, accepted: 1 };
    }

    // Notifications: Mark Read
    if (cleanUrl.match(/^\/api\/notifications\/[^/]+\/read$/)) {
      const id = cleanUrl.split('/')[3];
      try {
        await updateDoc(doc(db, 'notifications', id), { readAt: new Date().toISOString() });
      } catch {}
      return { ok: true };
    }

    // Notifications: Collection Reply ("Hazır dökümleri aldıracağım")
    if (cleanUrl.match(/^\/api\/notifications\/[^/]+\/collection$/)) {
      const id = cleanUrl.split('/')[3];
      const now = new Date().toISOString();
      try {
        await updateDoc(doc(db, 'notifications', id), { collectionRepliedAt: now });
        await addDoc(collection(db, 'notifications'), {
          title: 'Hazır Dökümlerini Aldıracak',
          body: `${this.user?.displayName || 'Müşteri'} hazır dökümlerini aldıracağını bildirdi.`,
          customerName: this.user?.displayName || 'Müşteri',
          branchId: this.getActiveBranchId(),
          sentAt: Date.now(),
          readAt: null
        });
      } catch {}
      document.dispatchEvent(new Event('notifications-changed'));
      return { ok: true };
    }

    // Account: Update Theme
    if (cleanUrl === '/api/account/theme') {
      const theme = body.theme || 'dark';
      localStorage.setItem('zirve-theme', theme);
      if (this.user) {
        this.user.theme = theme;
        try {
          await updateDoc(doc(db, getCollectionName('users'), this.user.id), { theme });
        } catch {}
      }
      document.dispatchEvent(new Event('theme-changed'));
      return { user: this.user };
    }

    // Account: Update Language
    if (cleanUrl === '/api/account/language') {
      const language = body.language || 'tr';
      localStorage.setItem('zirve-public-language', language);
      if (this.user) {
        this.user.language = language;
        try {
          await updateDoc(doc(db, getCollectionName('users'), this.user.id), { language });
        } catch {}
      }
      document.dispatchEvent(new Event('language-changed'));
      return { user: this.user };
    }

    // Account: Update Profile
    if (cleanUrl === '/api/account') {
      if (!this.user) throw new Error('Oturum açılmadı.');
      const updates = {};
      if (body.name) updates.displayName = body.name;
      if (body.username) updates.username = body.username;
      if (body.avatar) {
        const uploadKey = this.createUploadKey();
        const uploadRes = await this.uploadPhoto(body.avatar, uploadKey, () => {});
        updates.avatarUrl = uploadRes.photoUrl;
      }
      if (body.removeAvatar) updates.avatarUrl = null;

      try {
        await updateDoc(doc(db, getCollectionName('users'), this.user.id), updates);
        if (body.password && auth.currentUser) {
          await updatePassword(auth.currentUser, body.password);
        }
      } catch {}

      await this.refreshAccount();
      return { user: this.user };
    }

    return {};
  },

  async setTheme(theme) {
    const data = await this.mutate('/api/account/theme', 'PATCH', { theme });
    this.setUser(data.user);
    return data;
  },

  async setLanguage(language) {
    const data = await this.mutate('/api/account/language', 'PATCH', { language });
    this.setUser(data.user);
    return data;
  },

  setUser(account) {
    this.user = account;
  },

  uploadPhoto(photo, uploadKey, progress) {
    return new Promise(async (resolve, reject) => {
      try {
        progress(20);
        const fileName = `uploads/${Date.now()}_${uploadKey}.jpg`;
        const storageRef = ref(storage, fileName);

        let dataString = photo;
        if (!photo.startsWith('data:')) {
          dataString = `data:image/jpeg;base64,${photo}`;
        }

        progress(50);
        const snapshot = await uploadString(storageRef, dataString, 'data_url');
        progress(85);
        const downloadUrl = await getDownloadURL(snapshot.ref);
        uploadedPhotoCache.set(uploadKey, downloadUrl);
        progress(100);

        resolve({ uploadKey, photoUrl: downloadUrl });
      } catch (err) {
        console.error('Storage upload error:', err);
        // Fallback placeholder image if storage fails
        const fallbackUrl = '/assets/casting 2.png';
        uploadedPhotoCache.set(uploadKey, fallbackUrl);
        resolve({ uploadKey, photoUrl: fallbackUrl });
      }
    });
  }
};

export default Api;

export function readPhoto(file, maxMB = 5) {
  if (!file || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || !file.size || file.size > maxMB * 1024 * 1024) {
    return Promise.reject(new Error(`PNG, JPG veya WebP seçin. Fotoğraf en fazla ${maxMB} MB olabilir.`));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Fotoğraf okunamadı. Tekrar seçin.'));
    reader.readAsDataURL(file);
  });
}
